import React, { useEffect, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
  getWaivers,
  postParticipantBump,
  getParticipantInfo,
} from "../../../../services/participantServices/ParticipantService";
import Waiver from "../../../../interfaces/participant/Waiver";
import { ActiveParticipantDTO } from "../../../../interfaces/Participant";
import "./AddParticipantModal.css";

interface OptionType {
  value: string;
  label: string;
}

interface BumpParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  participant: ActiveParticipantDTO | null;
}

const ALLOWED_WAIVER_OPTIONS = ["OCDD-SW" , "OCDD-NOW" , "OCDD-ROW" , "OAAS-CCW"];

const BumpParticipantModal: React.FC<BumpParticipantModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  participant,
}) => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [waivers, setWaivers] = useState<Waiver[] | null>([]);
  const [waiverOptions, setWaiverOptions] = useState<OptionType[]>([]);
  const [selectedWaiver, setSelectedWaiver] = useState<string>("");
  const [participantData, setParticipantData] = useState<any>(null);

  useEffect(() => {
    const fetchWaivers = async () => {
      if (currentUser && isOpen) {
        const authToken: string = await currentUser.getIdToken();
        const waiversFetched = await getWaivers(authToken);
        if (waiversFetched.success) {
          setWaivers(waiversFetched.data);
          const formattedWaivers = waiversFetched.data.map((w: Waiver) => ({
            value: w.id.toString(),
            label: w.waiverName,
          }));

          const filteredFormattedWaivers = formattedWaivers?.filter((raw : OptionType) => {
            if(ALLOWED_WAIVER_OPTIONS?.includes(raw?.label)){
              return raw;
            }
          })
          setWaiverOptions(filteredFormattedWaivers);
        }
      }
    };
    fetchWaivers();
  }, [currentUser, isOpen]);
  

  useEffect(() => {
    const fetchParticipant = async () => {
      if (currentUser && isOpen && participant) {
        setIsFetching(true);
        try {
          const authToken = await currentUser.getIdToken();
          const response = await getParticipantInfo(participant.id.toString(), authToken);
          
          if (response && response !== null && response !== "unauthorized") {
            setParticipantData(response);
            const participantAny = response as any;
            if (participantAny.waiverTypeId) {
              setSelectedWaiver(participantAny.waiverTypeId.toString());
            } else if (participantAny.waiver?.id) {
              setSelectedWaiver(participantAny.waiver.id.toString());
            }
          }
        } catch (error: any) {
          toast.error(error?.message || "Failed to fetch participant data");
        } finally {
          setIsFetching(false);
        }
      }
    };
    fetchParticipant();
  }, [isOpen, participant, currentUser]);

  const handleWaiverChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWaiver(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!participant || !selectedWaiver) {
      toast.error("Please select a waiver");
      return;
    }

    setIsLoading(true);
    try {
      const authToken = await currentUser?.getIdToken();
      if (!authToken) {
        toast.error("Authentication error");
        return;
      }

      const response = await postParticipantBump(
        participant.id.toString(),
        selectedWaiver,
        authToken
      );

      if (response !== null) {
        toast.success("Participant waiver updated successfully!");
        handleCancel();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error("Failed to update participant waiver");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to update participant waiver");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedWaiver("");
    setParticipantData(null);
    onClose();
  };

  if (!isOpen || !participant) return null;

  const participantName = participantData 
    ? `${participantData.firstName || participant.firstName} ${participantData.lastName || participant.lastName}`
    : `${participant.firstName} ${participant.lastName}`;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
        onClick={handleCancel}
      >
        <div
          className="relative w-full max-w-[35rem] rounded-2xl bg-white shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 px-6">
            <div>
              <h2 className="" style={{
                color: "#0A0A0A",
                fontFamily: "Poppins",
                fontSize: "1.25rem",
                fontWeight: 700,
                lineHeight: "1.75rem"
              }}>
                Bump Participant Form
              </h2>
              <p className="m-0 mt-1 text-[#4A5565] font-poppins font-normal" style={{fontSize: "0.875rem", fontWeight: 400, lineHeight: "1.25rem"}}>
                Please use this form to change the participating waiver of a participant.
              </p>
            </div>
            {/* <button
              onClick={handleCancel}
              className="flex items-center text-[#0A0A0A] hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button> */}
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {isFetching ? (
              <div className="text-center p-8">
                <p className="text-[#6B7280]">Loading participant data...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Participant */}
                  <div>
                    <label className="block text-base font-semibold text-[#364153] mb-2">
                      Participant
                    </label>
                    <input
                      type="text"
                      value={participantName}
                      readOnly
                      className="w-full px-3 py-2 border border-[#D1D5DC] rounded-[0.625rem] text-base bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                  </div>

                  {/* Participant Waiver */}
                  <div>
                    <label className="block text-base font-semibold text-[#364153] mb-2">
                      Participant Waiver
                    </label>
                    <div className="relative">
                      <select
                        value={selectedWaiver}
                        onChange={handleWaiverChange}
                        className="w-full px-3 py-2 border border-[#D1D5DC] rounded-[0.625rem] text-base focus:outline-none focus:ring-1 focus:border-purple-500 focus:ring-purple-500 appearance-none bg-white"
                        required
                      >
                        <option value="">Select waiver</option>
                        {waiverOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-[#E5E7EB]">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 bg-[#F3F4F6] text-[#364153] font-poppins text-base font-medium rounded-[0.625rem] hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || !selectedWaiver}
                    className="px-6 py-2 bg-[#7E57C2] text-white font-poppins text-base font-medium rounded-[0.625rem] hover:bg-[#6D4CAB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BumpParticipantModal;

